// App.tsx - Updated with Supabase Debugger
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { HybridDataProvider } from './context/HybridDataProvider';
import { LocationProvider } from './context/LocationContext';
import { ProjectFlowProvider } from './context/ProjectFlowContext';
import { DemoModeProvider } from './context/DemoModeContext';
import { DemoBanner } from './components/DemoBanner';
import { HelmetProvider } from 'react-helmet-async';
import { useEffect } from 'react';
import { supabase } from './utils/supabaseClient';
import { startKeepAlive } from './services/projectAPI';
import { loadGoogleMaps } from './utils/loadGoogleMaps';
import './styles/luxury-theme.css';

import HomeQuestDashboard from './components/HomeQuestDashboard/HomeQuestDashboard';
import LoginPage from './pages/LoginPage';
// SignupPage is deprecated - signup is now handled in LoginPage
// import SignupPage from './pages/SignupPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import ProtectedRoute from './components/ProtectedRoute';
import QuickCallSystem from './components/Communications/QuickCallSystem';
import VoiceCallCenter from './components/VoiceCallCenter';
import PlatSearchPage from './pages/PlatSearchPage';
import Documents from './pages/Documents';
import VendorBiddingPage from './components/VendorBiddingPage';
import VendorBiddingTest from './components/VendorBiddingTest';
import PricingPage from './pages/PricingPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import LandingPage from './pages/LandingPage';
import FeaturesPage from './pages/FeaturesPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import BlogPage from './pages/BlogPage';
import BlogPostDetail from './pages/BlogPostDetail';
import HelpCenterPage from './pages/HelpCenterPage';
import DocumentationPage from './pages/DocumentationPage';
import StatusPage from './pages/StatusPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import SecurityPage from './pages/SecurityPage';
import SMSOptInPage from './pages/SMSOptInPage';
import TestWoodFloor from './components/TestWoodFloor';
import { ComponentErrorBoundary } from './components/ErrorBoundary';
import { Toaster } from 'sonner';
import PopupManager from './components/marketing/PopupManager';
// import TestInvoicePage from './pages/test-invoice.tsx';

console.log('APP.TSX LOADED');

// DEBUGGER COMPONENT TO FIND WHY YOU GET MOCK DATA
function SupabaseDebugger() {
  useEffect(() => {
    async function checkSupabase() {
      console.log('🚨 CHECKING WHY YOU GET MOCK DATA... 🚨');
      
      if (!supabase) {
        console.error('❌ SUPABASE IS NULL!');
        return;
      }
      
      console.log('✅ Supabase client exists');
      
      // Check auth
      const { data: { user } } = await supabase.auth.getUser();
      console.log('👤 Logged in as:', user?.email || 'Not logged in');
      
      // Check vendors table
      console.log('\n📊 CHECKING VENDORS TABLE...');
      try {
        const { data: vendors, error, count } = await supabase
          .from('vendors')
          .select('*', { count: 'exact' })
          .limit(5);
        
        if (error) {
          console.error('❌ VENDORS ERROR:', error);
          console.log('Fix with: ALTER TABLE vendors DISABLE ROW LEVEL SECURITY;');
        } else {
          console.log(`✅ Found ${count} vendors in database`);
          if (vendors && vendors.length > 0) {
            console.log('REAL VENDOR DATA:', vendors[0]);
            console.log('All vendors:', vendors);
          } else {
            console.warn('⚠️ VENDORS TABLE IS EMPTY!');
            console.log(`
INSERT INTO vendors (name, trade, lat, lng, rating, reviews, verified, hourly_rate, availability, address, phone, license, insurance, years_in_business)
VALUES 
('EarthWorks Pro LLC', 'Foundation', 34.0234, -84.6155, 4.8, 127, true, 85, 'Available', '123 Maple St, Kennesaw, GA', '770-555-0100', 'GC-12345', true, 15),
('Solid Foundation Inc', 'Foundation', 34.0334, -84.6255, 4.9, 89, true, 75, 'Available', '456 Oak Ave, Kennesaw, GA', '770-555-0101', 'FN-67890', true, 10);
            `);
          }
        }
      } catch (err) {
        console.error('❌ EXCEPTION:', err);
      }
      
      // Check projects table
      console.log('\n📊 CHECKING PROJECTS TABLE...');
      try {
        const { data: projects, error, count } = await supabase
          .from('projects')
          .select('*', { count: 'exact' })
          .limit(5);
        
        if (error) {
          console.error('❌ PROJECTS ERROR:', error);
        } else {
          console.log(`✅ Found ${count} projects in database`);
          if (projects && projects.length > 0) {
            console.log('REAL PROJECT DATA:', projects[0]);
          } else {
            console.warn('⚠️ PROJECTS TABLE IS EMPTY!');
            console.log(`
INSERT INTO projects (project_name, address, status, square_footage)
VALUES 
('Maple Street Residence', '123 Maple St, Kennesaw, GA', 'active', 4850),
('Oak Avenue Complex', '456 Oak Ave, Marietta, GA', 'planning', 12000);
            `);
          }
        }
      } catch (err) {
        console.error('❌ EXCEPTION:', err);
      }
      
      // Check all tables
      console.log('\n📋 ALL TABLES STATUS:');
      const tables = ['vendors', 'projects', 'profiles', 'teams', 'zoning_data', 'building_codes', 'parcels', 'parcel_boundaries'];
      
      for (const table of tables) {
        try {
          const { count, error } = await supabase
            .from(table)
            .select('*', { count: 'exact', head: true });
          
          if (error) {
            console.log(`❌ ${table}: ${error.message}`);
          } else {
            console.log(`${count > 0 ? '✅' : '⚠️'} ${table}: ${count} rows`);
          }
        } catch (err) {
          console.log(`❌ ${table}: ${err}`);
        }
      }
      
      console.log('\n🔥 IF TABLES ARE EMPTY, ADD DATA WITH SQL ABOVE! 🔥');
      console.log('🔍 Check HybridDataProvider.tsx - it might be using mock data!');
    }
    
    // Run the check
    checkSupabase();
    
    // Also log when providers mount
    console.log('📍 Providers mounted, checking data flow...');
  }, []);
  
  return null;
}

// Component to conditionally show marketing popups only on public pages
function MarketingPopups() {
  const location = useLocation();

  // Only show popups on marketing/landing pages, not in the app
  const marketingPages = ['/', '/features', '/about', '/contact', '/blog', '/pricing', '/help', '/docs'];
  const isMarketingPage = marketingPages.some(page =>
    location.pathname === page || location.pathname.startsWith(page + '/')
  );

  if (!isMarketingPage) {
    return null;
  }

  return (
    <PopupManager
      enableExitIntent={true}
      enableScroll={true}
      enableTimed={true}
    />
  );
}

function App() {
  // Start keep-alive to prevent cold starts
  useEffect(() => {
    console.log('🚀 App initialized, starting keep-alive service');
    startKeepAlive();
  }, []);

  // Load Google Maps for vendor discovery and other features
  useEffect(() => {
    console.log('🗺️ Loading Google Maps API...');
    loadGoogleMaps()
      .then(() => console.log('✅ Google Maps loaded successfully'))
      .catch((err) => console.error('❌ Failed to load Google Maps:', err));
  }, []);

  // Get auth state but don't block public pages
  const { loading } = useAuth();

  // Check if this is a public route that doesn't need auth
  const publicRoutes = ['/', '/features', '/about', '/contact', '/blog', '/help', '/docs', '/status', '/privacy', '/terms', '/security', '/sms-signup', '/pricing', '/login', '/signup']; // /signup redirects to /login
  const isPublicRoute = publicRoutes.some(route => window.location.pathname === route || window.location.pathname.startsWith(route));

  // Check if this is a vendor bidding route - if so, bypass all providers and auth
  const isVendorBiddingRoute = window.location.pathname.includes('/vendor-bidding/');

  if (isVendorBiddingRoute) {
    console.log('🔧 VENDOR BIDDING ROUTE DETECTED - BYPASSING AUTH');
    return (
      <>
        <Toaster position="top-right" richColors />
        <Routes>
        {/* Landing page */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostDetail />} />
        <Route path="/help" element={<HelpCenterPage />} />
        <Route path="/docs" element={<DocumentationPage />} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/sms-signup" element={<SMSOptInPage />} />
        <Route path="/test-wood-floor" element={<TestWoodFloor />} />

        {/* Vendor bidding routes - placed first to ensure they match */}
        <Route path="/vendor-bidding/:projectId" element={<VendorBiddingPage />} />
        <Route path="/vendor-bidding/:projectId/*" element={<VendorBiddingPage />} />
        <Route path="/project/:projectId/bid" element={<VendorBiddingPage />} />

        {/* Project dashboard route */}
        <Route path="/project/:projectId/*" element={<HomeQuestDashboard />} />
        <Route path="/project/:projectId" element={<HomeQuestDashboard />} />

        {/* Other routes */}
        <Route path="/login" element={<LoginPage />} />
        {/* Redirect /signup to /login since signup is now handled in LoginPage */}
        <Route path="/signup" element={<Navigate to="/login" replace />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/demo" element={<Navigate to="/project/00000000-0000-0000-0000-000000000001" replace />} />
        <Route path="/project/00000000-0000-0000-0000-000000000001" element={
          <DemoModeProvider>
            <DemoBanner />
            <HomeQuestDashboard />
          </DemoModeProvider>
        } />
        <Route path="/call" element={
          <ComponentErrorBoundary componentName="Quick Call System">
            <QuickCallSystem />
          </ComponentErrorBoundary>
        } />
        <Route path="/voice" element={
          <ComponentErrorBoundary componentName="Voice Call Center">
            <VoiceCallCenter />
          </ComponentErrorBoundary>
        } />
        <Route path="/plat" element={<PlatSearchPage />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        {/* <Route path="/test-invoice" element={<TestInvoicePage />} /> */}
        <Route
          path="/dashboard"
          element={
            localStorage.getItem('user')?.includes('kenwhite2015@gmail.com') ? (
              <HomeQuestDashboard />
            ) : (
              <ProtectedRoute>
                <HomeQuestDashboard />
              </ProtectedRoute>
            )
          }
        />
        <Route
          path="/projects/*"
          element={
            <ProtectedRoute>
              <HomeQuestDashboard />
            </ProtectedRoute>
          }
        />
      </Routes>
      </>
    );
  }

  // Only show loading spinner for protected routes, not public pages
  if (loading && !isPublicRoute) {
    return <div>Loading...</div>;
  }

  return (
    <HelmetProvider>
      <HybridDataProvider>
        <LocationProvider>
          <ProjectFlowProvider>
            <Toaster position="top-right" richColors />
          <SupabaseDebugger />

          {/* Marketing Popups - Only show on public marketing pages */}
          <MarketingPopups />

          <Routes>
            {/* Landing page */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostDetail />} />
            <Route path="/help" element={<HelpCenterPage />} />
            <Route path="/docs" element={<DocumentationPage />} />
            <Route path="/status" element={<StatusPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/sms-signup" element={<SMSOptInPage />} />

            {/* Vendor bidding routes - placed first to ensure they match */}
            <Route path="/vendor-bidding/:projectId" element={<VendorBiddingPage />} />
            <Route path="/vendor-bidding/:projectId/*" element={<VendorBiddingPage />} />
            <Route path="/project/:projectId/bid" element={<VendorBiddingPage />} />
            
            {/* Project dashboard route */}
            <Route path="/project/:projectId/*" element={<HomeQuestDashboard />} />
            <Route path="/project/:projectId" element={<HomeQuestDashboard />} />
            
            {/* Other routes */}
            <Route path="/login" element={<LoginPage />} />
            {/* Redirect /signup to /login since signup is now handled in LoginPage */}
            <Route path="/signup" element={<Navigate to="/login" replace />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/demo" element={<Navigate to="/project/00000000-0000-0000-0000-000000000001" replace />} />
            <Route path="/project/00000000-0000-0000-0000-000000000001" element={
              <DemoModeProvider>
                <DemoBanner />
                <HomeQuestDashboard />
              </DemoModeProvider>
            } />
            <Route path="/call" element={
              <ComponentErrorBoundary componentName="Quick Call System">
                <QuickCallSystem />
              </ComponentErrorBoundary>
            } />
            <Route path="/voice" element={
              <ComponentErrorBoundary componentName="Voice Call Center">
                <VoiceCallCenter />
              </ComponentErrorBoundary>
            } />
            <Route path="/plat" element={<PlatSearchPage />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            {/* <Route path="/test-invoice" element={<TestInvoicePage />} /> */}
            <Route
              path="/dashboard"
              element={
                localStorage.getItem('user')?.includes('kenwhite2015@gmail.com') ? (
                  <HomeQuestDashboard />
                ) : (
                  <ProtectedRoute>
                    <HomeQuestDashboard />
                  </ProtectedRoute>
                )
              }
            />
            <Route
              path="/projects/*"
              element={
                <ProtectedRoute>
                  <HomeQuestDashboard />
                </ProtectedRoute>
              }
            />
          </Routes>
        </ProjectFlowProvider>
      </LocationProvider>
    </HybridDataProvider>
    </HelmetProvider>
  );
}

export default App;